import dayjs from 'dayjs';
import { ReactNode, useMemo, useState } from 'react';
import { ArchivedRelationMarker } from 'src/modules/issuer-dashboard/components/portfolios/ArchivedRelationMarker.tsx';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import {
  conversionSourceAmount,
  inverseConversionRate,
} from '@/modules/common/utils/brokerConversion.ts';
import IssuerCorrespondenceItemModal from '@/modules/issuer-dashboard/components/modals/IssuerCorrespondenceItemModal.tsx';
import {
  IssuerAddUpdatePortfolioTransactionModal,
  listTypeWithBuy,
  listTypeWithSell,
} from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdatePortfolioTransactionModal.tsx';
import { IssuerPortfolioAssetFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioAssetFullInfoModal.tsx';
import { PortfolioTransactionTypeBlock } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioTransactionsTab.tsx';
import { useIssuerCommunicationsListQuery } from '@/modules/issuer-dashboard/queries/communications.ts';
import {
  useIssuerDeletePortfolioTransactionMutation,
  useIssuerPortfolioTransactionByIdQuery,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { BaseModalProps, ConfirmModal, ModalContentWrapper, Spinner } from 'ui';
import { goodFormatNumberCustom } from 'ui/utils';
import {
  CorrespondenceCommunicationActivityItem,
  PortfolioTransactionStatus,
  PortfolioTransactionType,
} from 'api';

export const InfoItem = ({ title, children }: { title: string; children: ReactNode }) => {
  return (
    <div className="w-full flex text-sm items-center gap-4 py-3 border-b">
      <div className="text-gray-500 min-w-[33%]">{title}</div>
      {children}
    </div>
  );
};

interface IssuerPortfolioTransactionFullInfoModalProps extends BaseModalProps {
  id: number;
  portfolioId: number;
}

export const IssuerPortfolioTransactionFullInfoModal = ({
  id,
  open,
  onClose,
}: IssuerPortfolioTransactionFullInfoModalProps) => {
  const [openEdit, setOpenEdit] = useState(false);
  const [openDel, setOpenDel] = useState(false);
  const [openAsset, setOpenAsset] = useState(false);
  const [openCorrespondence, setOpenCorrespondence] = useState(false);
  const { data: dataTransaction, isLoading } = useIssuerPortfolioTransactionByIdQuery({
    transactionId: id,
  });
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeletePortfolioTransactionMutation({
    onSuccess: () => {
      setOpenDel(false);
      onClose();
    },
  });

  const { data: dataCorrespondence } = useIssuerCommunicationsListQuery(
    {
      page: 1,
      page_size: 50,
      sender: dataTransaction?.transaction?.correspondence?.sender_email,
    },
    !dataTransaction?.transaction?.correspondence_id ||
      !dataTransaction?.transaction?.correspondence?.sender_email,
  );

  const correspondence = useMemo<CorrespondenceCommunicationActivityItem>(() => {
    const first = (dataCorrespondence?.pages || [])
      .flatMap(({ data }) => data)
      ?.filter(item => item.id === dataTransaction?.transaction?.correspondence_id)[0];

    return first;
  }, [dataCorrespondence?.pages, dataTransaction?.transaction?.correspondence_id]);

  const {
    asset,
    broker,
    to_bank,
    from_bank,
    to_currency,
    from_currency,
    fx_rate,
    is_conversion,
    transaction_type,
    ai_status,
    correspondence_id,
  } = dataTransaction?.transaction || {};
  const currency = (
    asset?.currency ||
    to_bank?.bank_currency ||
    from_bank?.bank_currency ||
    to_currency
  )?.toUpperCase();

  const isDividendReinvest =
    transaction_type === PortfolioTransactionType.IncomeDividendReinvestment;
  const isFee = transaction_type === PortfolioTransactionType.Fee;
  const isRevaluation = transaction_type === PortfolioTransactionType.Revaluation;
  const isSplit = transaction_type === PortfolioTransactionType.Split;
  const isBrokerDeposit =
    transaction_type === PortfolioTransactionType.BrokerDeposit && !is_conversion;
  const isBuy = transaction_type ? listTypeWithBuy.includes(transaction_type) : false;
  const isSell = transaction_type ? listTypeWithSell.includes(transaction_type) : false;
  const isBrokerSell = isSell && !!broker;

  const { amount, price_per_share, quantity, fee } = dataTransaction?.transaction || {};
  const conversionSpent = is_conversion ? conversionSourceAmount(amount, fx_rate) : null;
  const conversionPrice = is_conversion ? inverseConversionRate(fx_rate) : null;
  const archivedRelations = dataTransaction?.transaction?.archived_relations;
  const archivedNames = archivedRelations?.length
    ? archivedRelations.map(relation => `archived ${relation}`).join(', ')
    : undefined;

  return (
    <ModalContentWrapper
      variant="right"
      title={`Portfolio transaction #${id}`}
      open={open}
      onClose={onClose}
      onDelete={() => setOpenDel(true)}
      onEdit={() => setOpenEdit(true)}
      editDisabledReason={
        archivedNames ? `Restore the ${archivedNames} before editing this transaction.` : undefined
      }
      deleteDisabledReason={
        archivedNames
          ? `Restore the ${archivedNames} before archiving this transaction.`
          : undefined
      }
    >
      {isLoading && (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isLoading && dataTransaction?.transaction && (
        <>
          <div className="flex flex-col justify-between gap-4 h-full">
            <div className="flex flex-col gap-8 h-full">
              <div className="flex flex-col border-t">
                <InfoItem title="Type">
                  <div>
                    <PortfolioTransactionTypeBlock
                      type={dataTransaction?.transaction?.transaction_type}
                      isSettle={
                        dataTransaction?.transaction?.status ===
                        PortfolioTransactionStatus.Completed
                      }
                      isConversion={dataTransaction?.transaction?.is_conversion}
                      additionalInfo={
                        isSplit && quantity ? (quantity < 0 ? 'Reverse' : 'Forward') : undefined
                      }
                      aiStatus={ai_status}
                    />
                  </div>
                </InfoItem>
                {is_conversion && from_currency && to_currency && (
                  <>
                    <InfoItem title="Currency pair">
                      {from_currency.toUpperCase()} → {to_currency.toUpperCase()}
                    </InfoItem>
                    {amount != null && (
                      <InfoItem title="Quantity received">
                        {goodFormatNumberCustom(+amount, to_currency.toUpperCase(), 2, 2)}
                      </InfoItem>
                    )}
                    {conversionPrice != null && (
                      <InfoItem title="Price">
                        {goodFormatNumberCustom(conversionPrice, from_currency.toUpperCase(), 2, 2)}
                      </InfoItem>
                    )}
                    {conversionSpent != null && (
                      <InfoItem title="Total spent">
                        {goodFormatNumberCustom(conversionSpent, from_currency.toUpperCase(), 2, 2)}
                      </InfoItem>
                    )}
                  </>
                )}
                {asset?.name ? (
                  <InfoItem title="Asset">
                    <div
                      className={
                        asset.deleted_at
                          ? 'text-amber-700'
                          : 'text-blue-700 cursor-pointer hover:underline'
                      }
                      onClick={() => !asset.deleted_at && setOpenAsset(true)}
                    >
                      {asset?.name}
                    </div>
                    {asset.deleted_at && <ArchivedRelationMarker relation="asset" />}
                  </InfoItem>
                ) : (
                  !!broker?.name &&
                  !is_conversion && (
                    <InfoItem title="Broker">
                      <div
                        className={broker.deleted_at ? 'text-amber-700' : 'text-blue-700'}
                        // onClick={() => setOpenAsset(true)}
                      >
                        {broker?.name}
                      </div>
                      {broker.deleted_at && <ArchivedRelationMarker relation="broker" />}
                    </InfoItem>
                  )
                )}
                <InfoItem title="Status">
                  <div>
                    <ActivityStatusBadge status={dataTransaction?.transaction?.status} />
                  </div>
                </InfoItem>
                {(!!from_bank || !!to_bank || ((isBuy || isSell || is_conversion) && broker)) && (
                  <InfoItem title="Balance account">
                    {from_bank ? (
                      <span className="flex gap-2 items-center">
                        Bank:
                        <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                          <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                            <span
                              className={`${
                                from_bank.deleted_at ? 'text-amber-700' : 'text-gray-900'
                              } whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis`}
                            >
                              {from_bank?.bank_account_name}
                            </span>
                            {from_bank.deleted_at && <ArchivedRelationMarker relation="bank" />}
                          </span>
                          <span className="text-gray-400">
                            {from_bank?.bank_currency.toUpperCase()}
                          </span>
                        </span>
                      </span>
                    ) : to_bank ? (
                      <span className="flex gap-2 items-center">
                        Bank:
                        <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                          <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                            <span
                              className={`${
                                to_bank.deleted_at ? 'text-amber-700' : 'text-gray-900'
                              } whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis`}
                            >
                              {to_bank?.bank_account_name}
                            </span>
                            {to_bank.deleted_at && <ArchivedRelationMarker relation="bank" />}
                          </span>
                          <span className="text-gray-400">
                            {to_bank?.bank_currency.toUpperCase()}
                          </span>
                        </span>
                      </span>
                    ) : (isBuy || isSell || is_conversion) && broker ? (
                      <span className="flex gap-2 items-center">
                        Broker:
                        <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                          <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                            <span
                              className={`${
                                broker.deleted_at ? 'text-amber-700' : 'text-gray-900'
                              } whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis`}
                            >
                              {broker?.name}
                            </span>
                            {broker.deleted_at && <ArchivedRelationMarker relation="broker" />}
                          </span>
                          <span className="text-gray-400">{currency?.toUpperCase() || ''}</span>
                        </span>
                      </span>
                    ) : (
                      '-'
                    )}
                  </InfoItem>
                )}
                {!is_conversion && (
                  <InfoItem title="Balance">
                    <div>
                      {isRevaluation ||
                      isSplit ||
                      isDividendReinvest ||
                      Number(price_per_share) === 0 ? (
                        <>-</>
                      ) : !!amount && !!+amount ? (
                        <>
                          {isFee || isBrokerDeposit ? '-' : '+'}
                          {goodFormatNumberCustom(+amount, currency, 0, 2)}
                        </>
                      ) : (
                        <>
                          {to_bank || isBrokerSell ? '+' : '-'}
                          {goodFormatNumberCustom(
                            (Number(price_per_share) ?? 1) * (Number(quantity) ?? 1),
                            currency,
                            0,
                            2,
                          )}
                        </>
                      )}
                    </div>
                  </InfoItem>
                )}
                {!is_conversion && !!quantity && !!+quantity && (
                  <InfoItem title="Quantity">
                    {isRevaluation ? (
                      <>-</>
                    ) : (
                      <div>
                        {from_bank || isBuy || isDividendReinvest || (isSplit && +quantity >= 0)
                          ? '+'
                          : isSplit
                          ? ''
                          : '-'}
                        {quantity}
                      </div>
                    )}
                  </InfoItem>
                )}
                {!is_conversion && !!price_per_share && +price_per_share > 0 && (
                  <InfoItem title="Price">
                    <div>{goodFormatNumberCustom(+price_per_share || 0, currency, 2, 6)}</div>
                  </InfoItem>
                )}
                {!!fee && +fee > 0 && (
                  <InfoItem title="Fee">
                    <div>{goodFormatNumberCustom(+fee || 0, currency, 0, 2)}</div>
                  </InfoItem>
                )}
                <InfoItem title="Date and time">
                  <div>
                    {dataTransaction?.transaction?.confirmation_date
                      ? dayjs(dataTransaction.transaction.confirmation_date).format(
                          'DD MMM YYYY HH:mm',
                        )
                      : '-'}
                  </div>
                </InfoItem>
                {!!correspondence_id && (
                  <InfoItem title="Correspondence ID">
                    <span
                      className="text-blue-700 cursor-pointer hover:underline"
                      onClick={() => setOpenCorrespondence(true)}
                    >
                      {correspondence_id}
                    </span>
                  </InfoItem>
                )}
              </div>
              <div className="flex flex-col gap-4 w-full">
                {dataTransaction?.transaction?.memo && (
                  <>
                    <span className="font-medium text-gray-900 text-[16px] leading-[22px] font-[Inter]">
                      Memo
                    </span>
                    <span className="text-gray-900 text-sm font-[Inter]">
                      {dataTransaction?.transaction?.memo}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-4 w-full"></div>
          </div>
          {!!asset?.id && !!asset?.name && (
            <IssuerPortfolioAssetFullInfoModal
              id={asset?.id}
              name={asset?.name}
              portfolioId={dataTransaction?.transaction?.portfolio_id}
              open={openAsset}
              onClose={() => setOpenAsset(false)}
            />
          )}
          <IssuerAddUpdatePortfolioTransactionModal
            portfolioId={dataTransaction?.transaction?.portfolio_id}
            item={dataTransaction?.transaction}
            open={openEdit}
            onClose={() => setOpenEdit(false)}
          />
          <ConfirmModal
            title={<span>Archive transaction</span>}
            onConfirm={async () => {
              await mutateAsyncDelete(id);
            }}
            open={openDel}
            onClose={() => setOpenDel(false)}
          />
          {!!correspondence && (
            <IssuerCorrespondenceItemModal
              item={correspondence}
              onClose={() => setOpenCorrespondence(false)}
              open={openCorrespondence}
            />
          )}
        </>
      )}
    </ModalContentWrapper>
  );
};
